import React, { useState, useEffect } from 'react';
import { 
  Dog, 
  Search, 
  RefreshCw,
  Activity,
  AlertCircle,
  Clock,
  X,
  LogOut,
  User,
  Calendar
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS, getAuthHeaders } from '../config/api.ts';

interface Owner {
  _id: string;
  name: string;
  surname: string;
  email: string;
  phone: string;
}

interface ActiveCheckin {
  id: string;
  dog: {
    _id: string;
    name: string;
    breed: string;
    profileImage?: string;
    owner: {
      _id: string;
      name: string;
      surname: string;
      email: string;
      phone: string;
    };
  };
  branch: {
    _id: string;
    name: string;
  };
  staff: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    position: string;
  };
  serviceType: 'hotel' | 'daycare';
  checkInTime: string;
  status: string;
  notes?: string;
  duration?: number;
}

const SERVICE_TYPES = {
  hotel: { label: 'Otel', icon: '🏨', color: 'bg-blue-100 text-blue-800' },
  daycare: { label: 'Kreş', icon: '🏫', color: 'bg-green-100 text-green-800' }
};

const Aktiviteler: React.FC = () => {
  const [activeCheckins, setActiveCheckins] = useState<ActiveCheckin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    hotel: 0,
    daycare: 0
  });
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedCheckin, setSelectedCheckin] = useState<ActiveCheckin | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchActiveCheckins = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const headers = getAuthHeaders(token);
      
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.ACTIVE_DOGS}`,
        {
          method: 'GET',
          headers,
        }
      );

      const data = await response.json();
      
      if (data.success) {
        setActiveCheckins(data.data || []);
        
        const hotelCount = data.data.filter((c: ActiveCheckin) => c.serviceType === 'hotel').length;
        const daycareCount = data.data.filter((c: ActiveCheckin) => c.serviceType === 'daycare').length;
        
        setStats({
          total: data.data.length,
          hotel: hotelCount,
          daycare: daycareCount
        });
      } else {
        throw new Error(data.message || 'Aktif Kedi/Köpekler getirilemedi');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bilinmeyen hata');
      setActiveCheckins([]);
      setStats({
        total: 0,
        hotel: 0,
        daycare: 0
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveCheckins();
    setCurrentPage(1);
  }, [filterType]);

  // Filtreleme
  const filteredCheckins = activeCheckins.filter(checkin => {
    const matchesSearch = 
      checkin.dog.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (checkin.dog.owner && checkin.dog.owner.name && checkin.dog.owner.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (checkin.dog.owner && checkin.dog.owner.surname && checkin.dog.owner.surname.toLowerCase().includes(searchTerm.toLowerCase())) ||
      checkin.serviceType.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterType === 'all') return matchesSearch;
    return matchesSearch && checkin.serviceType === filterType;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCheckins.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCheckins.length / itemsPerPage);

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleCardClick = (type: string) => {
    if (selectedCard === type) {
      setSelectedCard(null);
      setFilterType('all');
    } else {
      setSelectedCard(type);
      setFilterType(type);
    }
  };

  const handleCheckinClick = (checkin: ActiveCheckin) => {
    setSelectedCheckin(checkin);
    setShowDetails(true);
  };

  const handleRefresh = () => {
    fetchActiveCheckins();
  };

  const handleCheckout = async (checkinId: string) => {
    if (!window.confirm('Bu köpeğin çıkışını yapmak istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setError('Admin token bulunamadı');
        return;
      }

      const headers = getAuthHeaders(token);
      
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.CHECKIN_CHECKOUT.replace('{id}', checkinId)}`,
        {
          method: 'PATCH',
          headers,
        }
      );

      const data = await response.json();
      
      if (data.success) {
        fetchActiveCheckins(); // Listeyi yenile
      } else {
        setError(data.message || 'Çıkış yapılamadı');
      }
    } catch (err) {
      setError('Çıkış yapılırken hata oluştu');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Aktif Kedi/Köpekler</h1>
              <p className="text-gray-600 mt-1">Şubedeki aktif check-in'ler</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefresh}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw size={16} />
                <span>Yenile</span>
              </button>
              <button
                onClick={() => {
                  const token = localStorage.getItem('adminToken');
                  if (token) {
                    // Token preview
                  }
                }}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                <span>🔑 Token Test</span>
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div 
              className={`bg-white rounded-lg shadow-sm border-2 p-6 cursor-pointer transition-all hover:shadow-md ${
                selectedCard === 'all' ? 'border-blue-200 bg-blue-50' : 'border-gray-200'
              }`}
              onClick={() => handleCardClick('all')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Toplam Aktif</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                  <p className="text-xs text-gray-500 mt-1">Detayları görüntüle →</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Dog size={24} className="text-blue-600" />
                </div>
              </div>
            </div>

            <div 
              className={`bg-white rounded-lg shadow-sm border-2 p-6 cursor-pointer transition-all hover:shadow-md ${
                selectedCard === 'hotel' ? 'border-blue-200 bg-blue-50' : 'border-gray-200'
              }`}
              onClick={() => handleCardClick('hotel')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Otel</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.hotel}</p>
                  <p className="text-xs text-gray-500 mt-1">Detayları görüntüle →</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🏨</span>
                </div>
              </div>
            </div>

            <div 
              className={`bg-white rounded-lg shadow-sm border-2 p-6 cursor-pointer transition-all hover:shadow-md ${
                selectedCard === 'daycare' ? 'border-green-200 bg-green-50' : 'border-gray-200'
              }`}
              onClick={() => handleCardClick('daycare')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Kreş</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.daycare}</p>
                  <p className="text-xs text-gray-500 mt-1">Detayları görüntüle →</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🏫</span>
                </div>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Kedi/Köpek adı, sahip adı veya hizmet türü ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2">
              <AlertCircle className="text-red-600" size={20} />
              <span className="text-red-800 font-bold">HATA: {error}</span>
            </div>
            <div className="mt-2 text-sm text-red-700">
              <p>🔍 Lütfen browser console'u açıp (F12) detaylı hata mesajını kontrol edin.</p>
              <p>🔑 Token Test butonuna tıklayarak authentication durumunu kontrol edin.</p>
            </div>
          </div>
        )}

        {/* Checkins Grid */}
        {currentItems.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Dog className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aktif Kedi/Köpek Bulunamadı</h3>
            <p className="text-gray-600">
              {searchTerm ? 'Arama kriterlerinize uygun aktif köpek bulunamadı.' : 'Şu anda aktif check-in bulunmuyor.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentItems.map((checkin) => {
              const serviceType = SERVICE_TYPES[checkin.serviceType];
              return (
                <div
                  key={checkin.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer transition-all hover:shadow-md"
                  onClick={() => handleCheckinClick(checkin)}
                >
                  {/* Card Header */}
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                          {checkin.dog.profileImage ? (
                            <img 
                              src={checkin.dog.profileImage} 
                              alt={checkin.dog.name}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <Dog size={24} className="text-gray-400" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{checkin.dog.name}</h3>
                          <p className="text-sm text-gray-500">{checkin.dog.breed}</p>
                        </div>
                      </div>
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${serviceType.color}`}>
                        {serviceType.icon} {serviceType.label}
                      </span>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-4">
                    <div className="space-y-3">
                      {/* Owner Info */}
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <User size={16} />
                        <span>
                          {checkin.dog.owner ? `${checkin.dog.owner.name || ''} ${checkin.dog.owner.surname || ''}`.trim() : 'Silinen Kullanıcı'}
                        </span>
                      </div>

                      {/* Staff Info */}
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Activity size={16} />
                        <span>{checkin.staff.name} ({checkin.staff.position})</span>
                      </div>

                      {/* Check-in Time */}
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Clock size={16} />
                        <span>{formatDate(checkin.checkInTime)} - {formatTime(checkin.checkInTime)}</span>
                      </div>

                      {/* Duration */}
                      {checkin.duration && (
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Calendar size={16} />
                          <span>{checkin.duration} saat</span>
                        </div>
                      )}

                                             {/* Notes */}
                       {checkin.notes && (
                         <div className="bg-gray-50 rounded-lg p-3">
                           <p className="text-sm text-gray-700">{checkin.notes}</p>
                         </div>
                       )}

                       {/* Checkout Button */}
                       <div className="mt-4 pt-4 border-t border-gray-100">
                         <button
                           onClick={() => handleCheckout(checkin.id)}
                           className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
                         >
                           <LogOut size={16} />
                           <span>Çıkış Yap</span>
                         </button>
                       </div>
                     </div>
                   </div>
                 </div>
               );
             })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center space-x-2 mt-8">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Önceki
            </button>
            <span className="px-3 py-2 text-sm text-gray-600">
              Sayfa {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Sonraki
            </button>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetails && selectedCheckin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Kedi/Köpek Detayları</h2>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-6">
                {/* Dog Info */}
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center">
                    {selectedCheckin.dog.profileImage ? (
                      <img 
                        src={selectedCheckin.dog.profileImage} 
                        alt={selectedCheckin.dog.name}
                        className="w-20 h-20 rounded-full object-cover"
                      />
                    ) : (
                      <Dog size={32} className="text-gray-400" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{selectedCheckin.dog.name}</h3>
                    <p className="text-gray-600">{selectedCheckin.dog.breed}</p>
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full mt-2 ${SERVICE_TYPES[selectedCheckin.serviceType].color}`}>
                      {SERVICE_TYPES[selectedCheckin.serviceType].icon} {SERVICE_TYPES[selectedCheckin.serviceType].label}
                    </span>
                  </div>
                </div>

                {/* Owner Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Sahip Bilgileri</h4>
                  <div className="space-y-1 text-sm">
                    <p><strong>Ad:</strong> {selectedCheckin.dog.owner ? `${selectedCheckin.dog.owner.name || ''} ${selectedCheckin.dog.owner.surname || ''}`.trim() : 'Silinen Kullanıcı'}</p>
                    <p><strong>E-posta:</strong> {selectedCheckin.dog.owner ? selectedCheckin.dog.owner.email : 'Hesap silindi'}</p>
                    <p><strong>Telefon:</strong> {selectedCheckin.dog.owner ? selectedCheckin.dog.owner.phone : 'Hesap silindi'}</p>
                  </div>
                </div>

                {/* Staff Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Personel Bilgileri</h4>
                  <div className="space-y-1 text-sm">
                    <p><strong>Ad:</strong> {selectedCheckin.staff.name}</p>
                    <p><strong>Pozisyon:</strong> {selectedCheckin.staff.position}</p>
                    <p><strong>E-posta:</strong> {selectedCheckin.staff.email}</p>
                    <p><strong>Telefon:</strong> {selectedCheckin.staff.phone}</p>
                  </div>
                </div>

                {/* Check-in Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Check-in Detayları</h4>
                  <div className="space-y-1 text-sm">
                    <p><strong>Giriş Tarihi:</strong> {formatDate(selectedCheckin.checkInTime)}</p>
                    <p><strong>Giriş Saati:</strong> {formatTime(selectedCheckin.checkInTime)}</p>
                    {selectedCheckin.duration && (
                      <p><strong>Süre:</strong> {selectedCheckin.duration} saat</p>
                    )}
                    <p><strong>Durum:</strong> {selectedCheckin.status}</p>
                  </div>
                </div>

                {/* Notes */}
                {selectedCheckin.notes && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Notlar</h4>
                    <p className="text-sm text-gray-700">{selectedCheckin.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Aktiviteler;
